import React, { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ISlidePasteProps, Off, ISectionNum, PanelType } from '../interface';
import { pasteSpecialEventBus } from '@src/slide/application/service/copy-paste/core/paste/paste-special/event-bus';
import { ICopyInfo } from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import PasteSpecialPanel from '../index';
import { reaction } from 'mobx';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';

/**
 * 幻灯片整页复制，选择性粘贴面板
 */
const SlidePasteSpecialPanel = (props: ISlidePasteProps) => {
    const [isShow, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const copyInfoRef = useRef({}) as MutableRefObject<ICopyInfo>;
    const undoRedoRef = useRef(() => {}) as MutableRefObject<Off>;
    const sectionRef = useRef({ sectionNum: 0, slideNum: 0 }) as MutableRefObject<ISectionNum>;
    const editorAppService = AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    const menuClickStatusRef = useRef<boolean>(false);
    const getPosition = () => {
        const { sectionNum, slideNum } = sectionRef.current;
        const { insertSlideIndex = 0 } = copyInfoRef.current;
        const left = props.sectionWidth - 50;
        // 粘贴位置在第一个幻灯片上方的间隙  或者 不存在节
        if (!insertSlideIndex || !sectionNum) {
            return {
                top: props.slideHeight * (insertSlideIndex + 1),
                left,
            };
        }
        // 节存在
        const sectionHeight = 24;
        const sectionTotalHeight = sectionHeight * sectionNum;
        const slideTotalHeight = props.slideHeight * (slideNum + 1);
        return {
            top: slideTotalHeight + sectionTotalHeight,
            left,
        };
    };

    // 组件挂载后，注册事件
    useEffect(() => {
        // 监听粘贴完成，展示面板
        const disposePasteEvent = pasteSpecialEventBus.on(PanelType.SLIDE_PASTE_PANEL, (copyInfo: ICopyInfo) => {
            copyInfoRef.current = copyInfo;
            // 需要根据copyInfo的变化，找到对应的elementModel，计算left 和 top
            const newPosition = getPosition();
            setPosition(newPosition);
            setShow(true);

            // 粘贴幻灯片，图标曝光埋点
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PASTE_SPECIAL,
                action: TDWActionType.SLIDE_EXPOSURE,
            });
        });
        // 监听undoRedo
        undoRedoRef.current = onUndoRedo();

        // 进入母版编辑后，面板消失
        const disposeMasterChange = reaction(
            () => editorAppService.editableServiceLayer?.masterLayoutEditService.isMasterEditType,
            (isMasterEditType) => {
                if (!isMasterEditType) return;
                setShow(false);
            },
        );
        return () => {
            // 组件销毁时取消监听
            disposePasteEvent();
            disposeMasterChange();
            undoRedoRef.current?.();
        };
    }, []);

    useEffect(() => {
        sectionRef.current = {
            sectionNum: props.sectionNum,
            slideNum: props.slideNum,
        };
    }, [props.sectionNum, props.slideNum]);

    const onUndoRedo = (): Off => {
        const sdkCommandEvents = SlideDataServices.SDKDataServices.EventManager()?.commandEvents;
        return (
            sdkCommandEvents?.onUndoRedoChange(() => {
                // 菜单展示时，选中粘贴策略后会触发undo操作，但此时不应该隐藏按钮
                if (menuClickStatusRef.current) return;
                setShow(false);
            }) ?? (() => {})
        );
    };


    const beforePaste = useCallback(() => {
        menuClickStatusRef.current = true;
    }, []);

    const afterPaste = useCallback(() => {
        menuClickStatusRef.current = false;
    }, []);


    return isShow ? (
        <PasteSpecialPanel
            position={position}
            copyInfo={copyInfoRef.current}
            serviceDelegates={{
                selectionService: editorAppService.viewableServiceLayer.selectionService,
                uiDataService: editorAppService.getUiDataService(),
                undoRedoService: editorAppService.editableServiceLayer?.undoRedoService,
            }}
            beforePaste={beforePaste}
            afterPaste={afterPaste}
        />
    ) : null;
};

export default React.memo(SlidePasteSpecialPanel);
